import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';

/**
 * CVV mask directive that provides digits-only input behavior.
 * Allows 3 or 4 digits (4 for Amex cards).
 *
 * Usage:
 * ```html
 * <input appCvvMask [(ngModel)]="cvv" />
 * <input appCvvMask [maxLength]="4" [(ngModel)]="cvv" /> <!-- For Amex -->
 * ```
 *
 * The directive exports raw digits to ngModel.
 */
@Directive({
  selector: '[appCvvMask]',
  standalone: true,
  host: {
    inputmode: 'numeric',
    autocomplete: 'cc-csc',
    // Remove static maxlength from host - now dynamic via Input
  },
})
export class CvvMaskDirective implements OnInit, OnChanges {
  private readonly _el = inject(ElementRef<HTMLInputElement>);

  /**
   * Maximum CVV length. Defaults to 4 (accommodates Amex).
   * Set to 3 for non-Amex cards.
   */
  @Input() maxLength = 4;

  ngOnInit(): void {
    // Set initial maxlength attribute
    this._el.nativeElement.setAttribute('maxlength', String(this.maxLength));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxLength'] && !changes['maxLength'].firstChange) {
      // Update native element maxlength attribute
      this._el.nativeElement.setAttribute('maxlength', String(this.maxLength));

      // If current value exceeds new maxLength, truncate
      const currentValue = this._el.nativeElement.value;
      if (currentValue.length > this.maxLength) {
        this._el.nativeElement.value = currentValue.slice(0, this.maxLength);
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // Extract only digits, respect maxLength
    const rawDigits = input.value.replace(/\D/g, '').slice(0, this.maxLength);

    // Only update if needed
    if (input.value !== rawDigits) {
      const cursorPosition = input.selectionStart || 0;

      // Update input value
      input.value = rawDigits;

      // Restore cursor position (adjusted for any removed characters)
      const newPosition = Math.min(cursorPosition, rawDigits.length);
      requestAnimationFrame(() => {
        input.setSelectionRange(newPosition, newPosition);
      });
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    const isCtrlCmd = event.ctrlKey || event.metaKey;
    const allowedWithCtrl = ['a', 'c', 'v', 'x'];
    // Allow: home, end, left, right arrows
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      allowedKeys.includes(event.key) ||
      navigationKeys.includes(event.key) ||
      (isCtrlCmd && allowedWithCtrl.includes(event.key.toLowerCase()))
    ) {
      return;
    }

    // Allow numbers only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }
}
